// Human-readable renderer for audit log entries. Returns a short summary
// string per row plus an icon name; the page composes the actor/target
// emphasis around it.
import { formatBytes } from './bytes';
function fmtDuration(s) {
    if (typeof s !== 'number')
        return '';
    return s < 60 ? `${s.toFixed(0)}s` : `${Math.round(s / 60)}m`;
}
function asString(v, fallback = '') {
    return typeof v === 'string' ? v : fallback;
}
function asNum(v) {
    return typeof v === 'number' ? v : null;
}
function asArray(v) {
    return Array.isArray(v) ? v : [];
}
function target(row) {
    return row.target_name ?? (row.target_id ? row.target_id.slice(0, 8) : '—');
}
function cronToHuman(cron) {
    if (cron === '0 */4 * * *')
        return 'every 4h';
    if (cron === '0 */6 * * *')
        return 'every 6h';
    if (cron === '0 */12 * * *')
        return 'every 12h';
    if (cron === '0 */2 * * *')
        return 'every 2h';
    if (cron === '0 0 * * *')
        return 'daily';
    return cron;
}
function diffToString(before, after) {
    const parts = [];
    for (const k of Object.keys(after)) {
        const b = before[k];
        const a = after[k];
        if (k === 'schedule_cron') {
            parts.push(`schedule ${cronToHuman(String(b))} → ${cronToHuman(String(a))}`);
        }
        else {
            parts.push(`${k} \`${String(b)}\` → \`${String(a)}\``);
        }
    }
    return parts.join(', ');
}
/**
 * Choose a short human summary for any audit row. The rendering function
 * deliberately strips actor (rendered separately by the page) — its job is
 * just the verb + target + structured metadata bits.
 */
export function renderAudit(row) {
    const meta = row.audit_metadata || {};
    const tgt = target(row);
    switch (row.action) {
        case 'database.create': {
            const storages = asArray(meta.storages);
            const host = asString(meta.host);
            const dbname = asString(meta.dbname);
            const schedule = cronToHuman(asString(meta.schedule));
            const parts = [];
            if (host && dbname)
                parts.push(`${host}/${dbname}`);
            if (schedule)
                parts.push(schedule);
            if (storages.length)
                parts.push(`${storages.length} storages`);
            return {
                summary: `created database **${tgt}**`,
                detail: parts.join(' · ') || undefined,
                icon: 'plus',
            };
        }
        case 'database.update': {
            const before = meta.before || {};
            const after = meta.after || {};
            return {
                summary: `changed **${tgt}**: ${diffToString(before, after)}`,
                icon: 'edit',
            };
        }
        case 'database.delete':
            return { summary: `deleted database **${tgt}**`, icon: 'trash' };
        case 'storage.create': {
            const kind = asString(meta.kind);
            const endpoint = asString(meta.endpoint_url);
            const bucket = asString(meta.bucket);
            return {
                summary: `added storage **${tgt}** (${kind || 's3'})`,
                detail: endpoint && bucket ? `${endpoint} → ${bucket}` : undefined,
                icon: 'plus',
            };
        }
        case 'storage.update': {
            const before = meta.before || {};
            const after = meta.after || {};
            return {
                summary: `updated storage **${tgt}**: ${diffToString(before, after)}`,
                icon: 'edit',
            };
        }
        case 'storage.delete':
            return { summary: `deleted storage **${tgt}**`, icon: 'trash' };
        case 'backup.create.manual':
            return { summary: `started backup of **${tgt}**`, icon: 'play' };
        case 'backup.create.scheduled':
            return {
                summary: `scheduler enqueued backup of **${tgt}**`,
                detail: asString(meta.cron) || undefined,
                icon: 'clock',
            };
        case 'backup.succeeded': {
            const size = asNum(meta.size_bytes);
            const dur = fmtDuration(meta.duration_seconds);
            const locs = asArray(meta.locations);
            const bits = [];
            if (size !== null)
                bits.push(formatBytes(size));
            if (dur)
                bits.push(dur);
            bits.push(`${locs.length}/${locs.length} locations`);
            return {
                summary: `backup of **${tgt}** succeeded`,
                detail: bits.join(' · '),
                icon: 'backups',
            };
        }
        case 'backup.partial': {
            const size = asNum(meta.size_bytes);
            const uploaded = asArray(meta.uploaded);
            const failed = asArray(meta.failed);
            const bits = [];
            if (size !== null)
                bits.push(formatBytes(size));
            bits.push(`${uploaded.length}/${uploaded.length + failed.length} uploaded`);
            if (failed.length)
                bits.push(`failed: ${failed.map((f) => f.storage).join(', ')}`);
            return {
                summary: `backup of **${tgt}** partial`,
                detail: bits.join(' · '),
                icon: 'warn',
            };
        }
        case 'backup.failed': {
            const err = asString(meta.error);
            return {
                summary: `backup of **${tgt}** failed`,
                detail: err || undefined,
                icon: 'x',
            };
        }
        case 'backup.soft_delete':
            return { summary: `soft-deleted backup of **${tgt}**`, icon: 'trash' };
        case 'backup.purge.requested': {
            const locs = asNum(meta.locations_pending_delete);
            return {
                summary: `purged backup of **${tgt}**`,
                detail: locs !== null ? `${locs} locations queued for storage delete` : undefined,
                icon: 'trash',
            };
        }
        case 'backup.download.requested':
            return { summary: `requested download of backup of **${tgt}**`, icon: 'download' };
        case 'backup.download.delivered':
            return { summary: `downloaded backup of **${tgt}**`, icon: 'download' };
        case 'restore.initiated': {
            const host = asString(meta.target_host);
            const dbname = asString(meta.target_dbname);
            return {
                summary: `initiated restore of backup of **${tgt}**`,
                detail: host && dbname ? `→ ${host}/${dbname}` : undefined,
                icon: 'restore',
            };
        }
        case 'restore.completed':
            return {
                summary: `restore of **${tgt}** completed`,
                detail: `→ ${asString(meta.target_host)}/${asString(meta.target_dbname)}`,
                icon: 'restore',
            };
        case 'restore.failed':
            return {
                summary: `restore of **${tgt}** failed`,
                detail: asString(meta.error) || undefined,
                icon: 'x',
            };
        case 'retention.sweep.completed': {
            const deleted = asNum(meta.deleted_locations) ?? 0;
            const purged = asNum(meta.purged_backups) ?? 0;
            const dur = fmtDuration(meta.duration_seconds);
            const errs = asArray(meta.errors).length;
            const detailParts = [];
            if (deleted)
                detailParts.push(`${deleted} storage objects`);
            if (purged)
                detailParts.push(`${purged} backup rows`);
            if (dur)
                detailParts.push(dur);
            if (errs)
                detailParts.push(`${errs} errors`);
            return {
                summary: 'retention sweep',
                detail: detailParts.join(' · ') || 'no work',
                icon: 'refresh',
            };
        }
        case 'connection.test.db': {
            const ok = meta.result === 'ok';
            const latency = asNum(meta.latency_ms);
            const ver = asString(meta.server_version);
            const bits = [];
            if (latency !== null)
                bits.push(`${latency}ms`);
            if (ok && ver)
                bits.push(`PostgreSQL ${ver}`);
            if (!ok)
                bits.push(asString(meta.error, 'failed'));
            return {
                summary: `tested DB connection to **${tgt}**`,
                detail: bits.join(' · '),
                icon: ok ? 'test' : 'x',
            };
        }
        case 'connection.test.storage': {
            const ok = meta.result === 'ok';
            const latency = asNum(meta.latency_ms);
            const bits = [];
            if (latency !== null)
                bits.push(`${latency}ms`);
            bits.push(`read ${meta.can_head ? 'ok' : 'fail'}`);
            bits.push(`write ${meta.can_write ? 'ok' : 'fail'}`);
            if (!ok && meta.error)
                bits.push(asString(meta.error));
            return {
                summary: `tested storage reachability to **${tgt}**`,
                detail: bits.join(' · '),
                icon: ok ? 'test' : 'x',
            };
        }
        // ─── Metadata self-backup ─────────────────────────────────────────
        // Off-site dumps of the metadata DB. Surfaced here so the /audit page
        // shows human text instead of `meta.self_backup.failed on metadata-backup`.
        case 'meta.self_backup.completed': {
            const size = asNum(meta.ciphertext_size_bytes);
            const dur = fmtDuration(meta.duration_seconds);
            const bits = [];
            if (size !== null)
                bits.push(formatBytes(size));
            if (dur)
                bits.push(dur);
            return {
                summary: 'metadata backup completed',
                detail: bits.join(' · ') || undefined,
                icon: 'shield',
            };
        }
        case 'meta.self_backup.failed':
            return {
                summary: 'metadata backup FAILED',
                detail: asString(meta.error) || undefined,
                icon: 'x',
            };
        case 'system.meta_backup.run_now':
            return {
                summary: 'metadata backup triggered manually',
                icon: 'play',
            };
        case 'system.meta_backup.config_updated': {
            const changes = meta.changes || {};
            const parts = Object.entries(changes).map(([k, v]) => `${k}: ${v.old} → ${v.new}`);
            return {
                summary: 'metadata backup settings changed',
                detail: parts.join('; ') || undefined,
                icon: 'sliders',
            };
        }
        default:
            return {
                summary: `${row.action} on ${tgt}`,
                icon: 'info',
            };
    }
}
/**
 * Convert "**bold**" markers in summary to ReactNode-compatible parts.
 * Kept simple — only "**...**" runs are interpreted, no nested formatting.
 */
export function splitBold(s) {
    const parts = [];
    const re = /\*\*([^*]+)\*\*/g;
    let last = 0;
    let m;
    while ((m = re.exec(s))) {
        if (m.index > last)
            parts.push({ text: s.slice(last, m.index), bold: false });
        parts.push({ text: m[1], bold: true });
        last = re.lastIndex;
    }
    if (last < s.length)
        parts.push({ text: s.slice(last), bold: false });
    return parts;
}
